import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  DatePicker,
  message,
  Tag,
} from "antd";
import { fetchPouch } from "./FetchPouch";
import moment from "moment";
import { EditOutlined } from "@ant-design/icons";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { useSelector } from "react-redux";
import { token } from "../../constants/Constants";
import { create_pouch, update_pouch } from "../../endpoints/API";

const Pouch = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPouch, setEditingPouch] = useState(null);
  const [form] = Form.useForm();
  const [pouch, setPouch] = useState([]);

  useEffect(() => {
    const getPouches = async () => {
      try {
        await fetchPouch(setPouch, baseUrl, prodTestUrl);
      } catch (error) {
        message.error(error?.response?.data?.error || "Failed to fetch pouch.");
      }
    };
    getPouches();
  }, []);

  const columns = [
    { title: "Barcode", dataIndex: "pouch_barcode", key: "pouch_barcode" },
    {
      title: "Start Date Time",
      dataIndex: "start_date_time",
      key: "start_date_time",
      render: (dateTime) => (
        <>
          <Tag>{dateTime ? moment(dateTime).format("DD-MM-YYYY") : ""}</Tag>
          <Tag>{dateTime ? moment(dateTime).format("hh:mm A") : ""}</Tag>
        </>
      ),
    },
    {
      title: "End Date Time",
      dataIndex: "end_date_time",
      key: "end_date_time",
      render: (dateTime) => (
        <>
          <Tag>{dateTime ? moment(dateTime).format("DD-MM-YYYY") : ""}</Tag>
          <Tag>{dateTime ? moment(dateTime).format("hh:mm A") : ""}</Tag>
        </>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => handleEditPouch(record)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  const handleEditPouch = (pouch) => {
    setEditingPouch(pouch);
    setIsEditMode(true);
    setIsModalOpen(true);
    form.setFieldsValue({
      pouch_barcode: pouch.pouch_barcode,
      startDateTime: moment(pouch.start_date_time),
      endDateTime: moment(pouch.end_date_time),
    });
  };

  const handleCreateOrEditPouch = async (values) => {
    const incrementDateByOne = (date) => {
      const newDate = new Date(date);
      newDate.setDate(newDate.getDate() + 1);
      return newDate.toISOString();
    };

    const requestData = {
      pouch_barcode: values.pouch_barcode,
      start_date_time: incrementDateByOne(values.startDateTime),
      end_date_time: incrementDateByOne(values.endDateTime),
      status: "enable",
    };

    try {
      if (isEditMode && editingPouch) {
        const response = await update_pouch(editingPouch.id, requestData);
        if (response.statusText == "OK") {
          message.success("Pouch updated successfully!");
          await fetchPouch(setPouch, baseUrl, prodTestUrl);
        } else {
          const errorData = response?.data;
          message.error("Failed to update pouch: " + errorData.message);
        }
      } else {
        const response = await create_pouch(requestData);
        if (response.statusText == "OK") {
          message.success("Pouch created successfully!");
        } else {
          const errorData = response?.data;
          message.error("Failed to create pouch: " + errorData.message);
        }
      }
      await fetchPouch(setPouch, baseUrl, prodTestUrl);
    } catch (error) {
      message.error(
        error?.response?.data?.error ||
          "An error occurred while saving the pouch."
      );
    } finally {
      setIsModalOpen(false);
      setIsEditMode(false);
      setEditingPouch(null);
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingPouch(null);
    form.resetFields();
  };

  return (
    <div>
      {/* Header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          Create Pouch
        </Button>
      </div>

      {/* Scrollable table wrapper */}
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table
          dataSource={pouch}
          columns={columns}
          rowKey={(record) => record.id}
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        title={isEditMode ? "Edit Pouch" : "Create Pouch"}
        visible={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width="min(520px, 95vw)"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateOrEditPouch}>
          <Form.Item
            label="Barcode"
            name="pouch_barcode"
            rules={[{ required: true, message: "Please enter the barcode!" }]}
          >
            <Input placeholder="Enter Barcode" />
          </Form.Item>

          <Form.Item
            label="Start Date Time"
            name="startDateTime"
            rules={[
              { required: true, message: "Please select the start date and time!" },
            ]}
          >
            <DatePicker
              showTime
              placeholder="Select Start Date and Time"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="End Date Time"
            name="endDateTime"
            rules={[
              { required: true, message: "Please select the end date and time!" },
            ]}
          >
            <DatePicker
              showTime
              placeholder="Select End Date and Time"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button type="primary" htmlType="submit">
                {isEditMode ? "Update" : "Submit"}
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Pouch;